import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, ApiError } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { useBrand } from "@/brand/BrandProvider";

/** Staff sign-in: password, or a one-time code sent to the account's e-mail. */
export function Login() {
  const { login, loginWithToken } = useAuth();
  const brand = useBrand();
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const [mode, setMode] = useState<"password" | "code">("password");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [debug, setDebug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      if (mode === "password") { await login(email, password); nav("/app", { replace: true }); return; }
      if (!sent) {
        const r = await api<{ sent: boolean; debug_code?: string }>("/auth/otp/request", { method: "POST", body: JSON.stringify({ email }) });
        setSent(true); setDebug(r.debug_code ?? null); return;
      }
      const r = await api<{ access_token: string }>("/auth/otp/verify", { method: "POST", body: JSON.stringify({ email, code }) });
      await loginWithToken(r.access_token);
      nav("/app", { replace: true });
    } catch (err) {
      setError(err instanceof ApiError && err.status === 429 ? t("auth.tooMany") : mode === "code" && sent ? t("auth.invalidCode") : t("auth.invalid"));
    } finally { setBusy(false); }
  }
  const cls = (on: boolean) => `flex-1 rounded-lg px-2 py-1 text-xs ${on ? "bg-accent text-white" : "bg-surface-2 text-ink"}`;
  return (
    <main className="min-h-screen flex items-center justify-center p-4">
      <form onSubmit={submit} className="w-full max-w-sm bg-surface rounded-modal border shadow-pop p-6 space-y-4">
        <h1 className="text-xl font-semibold text-accent">{brand?.name[i18n.language] ?? brand?.name.el ?? t("app.title")}</h1>
        <div className="flex gap-1 rounded-lg bg-surface-2 p-1">
          <button type="button" className={cls(mode === "password")} onClick={() => { setMode("password"); setError(null); }}>{t("auth.withPassword")}</button>
          <button type="button" className={cls(mode === "code")} onClick={() => { setMode("code"); setSent(false); setCode(""); setError(null); }}>{t("auth.withCode")}</button>
        </div>
        <label className="block text-sm">{t("auth.email")}
          <input className="mt-1 w-full rounded-lg border px-3 py-2" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required disabled={mode === "code" && sent} />
        </label>
        {mode === "password" && (
          <label className="block text-sm">{t("auth.password")}
            <input className="mt-1 w-full rounded-lg border px-3 py-2" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </label>
        )}
        {mode === "code" && sent && (
          <label className="block text-sm">{t("auth.code")}
            <input className="mt-1 w-full rounded-lg border px-3 py-2 text-center text-lg tracking-[0.4em]" inputMode="numeric" autoComplete="one-time-code" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))} required />
            <span className="mt-1 block text-xs text-muted">{t("auth.codeSent")}{debug ? ` · ${t("auth.debugCode")}: ${debug}` : ""}</span>
          </label>
        )}
        {error && <p className="text-sm text-danger" role="alert">{error}</p>}
        <button disabled={busy} className="w-full rounded-lg bg-accent text-white py-2 font-medium disabled:opacity-50">{mode === "password" ? t("auth.login") : sent ? t("auth.verify") : t("auth.sendCode")}</button>
        {mode === "code" && sent && <button type="button" className="w-full text-xs text-muted underline" onClick={() => { setSent(false); setCode(""); }}>{t("auth.resend")}</button>}
        <div className="text-xs text-muted text-center">
          <button type="button" className="underline" onClick={() => i18n.changeLanguage(i18n.language === "el" ? "en" : "el")}>{i18n.language === "el" ? "English" : "Ελληνικά"}</button>
        </div>
      </form>
    </main>
  );
}
